import { useEffect, useState } from 'react'

import { fehlertext } from '../../api/client'
import { rueckfrage } from '../../components/Rueckfrage'
import { useMaintenanceOff, useMaintenanceOn, useMaintenanceState, useMe } from '../../hooks/queries'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { useOnline } from '../../hooks/useOutbox'
import { useTexte } from '../../texte'

/** An ISO moment as the value of `<input type="datetime-local">` -- the device's clock. */
function asFieldValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const moment = new Date(iso)
  if (Number.isNaN(moment.getTime())) return ''
  const two = (n: number) => String(n).padStart(2, '0')
  return `${moment.getFullYear()}-${two(moment.getMonth() + 1)}-${two(moment.getDate())}T${two(moment.getHours())}:${two(moment.getMinutes())}`
}

/**
 * The maintenance page, switched by the supervisor (#131, 1.114.0).
 *
 * **Two scopes, from use:** everyone but the supervisor (the migration), or
 * only the readers -- authors test the new version before everyone sees it.
 * What the scope means is decided on the server (`Maintenance::lets()`).
 *
 * **Switching on hands this device a pass** (a cookie the server signs), so
 * the supervisor keeps working while everyone else waits; the card names the
 * emergency exit for the day the pass is gone.
 */
export function MaintenanceCard() {
  const texte = useTexte()
  const t = texte.betrieb.maintenance
  const me = useMe()
  const online = useOnline()
  const limits = useTextgrenzen()
  const state = useMaintenanceState(me.data?.may_operate === true)
  const switchOn = useMaintenanceOn()
  const switchOff = useMaintenanceOff()

  const [scope, setScope] = useState<'all' | 'readers'>('all')
  const [until, setUntil] = useState('')
  const [text, setText] = useState('')

  // What the server has is what the form starts with -- also after a change.
  const active = state.data?.active === true
  useEffect(() => {
    if (state.data === undefined) return
    setScope(state.data.scope ?? 'all')
    setUntil(asFieldValue(state.data.until))
    setText(state.data.text ?? '')
  }, [state.data])

  if (state.isPending) return <p className="card__lead leise">…</p>
  if (state.isError) {
    return (
      <p className="notice" role="alert">
        {fehlertext(state.error)}
      </p>
    )
  }

  const shown = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString(texte.allgemein.gebiet, { dateStyle: 'medium', timeStyle: 'short' }) : ''
  const length = [...text].length
  const tooLong = length > limits.maintenance_text
  const busy = switchOn.isPending || switchOff.isPending
  const error = switchOn.error ?? switchOff.error

  async function save() {
    if (!active && !(await rueckfrage(t.askOn, t.askOnShort))) return
    switchOn.mutate({ scope, until: until === '' ? null : new Date(until).toISOString(), text })
  }

  return (
    <div className="card card--seite" data-maintenance={active ? 'on' : 'off'}>
      <p className="card__lead">{t.lead}</p>

      <p className="fett" data-maintenance-state="">
        {active ? t.on : t.off}
      </p>
      {active && (
        <>
          {state.data.since && <p className="leise">{t.since(shown(state.data.since))}</p>}
          {state.data.until && <p className="leise">{t.until(shown(state.data.until))}</p>}
        </>
      )}

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          if (online && !busy && !tooLong) void save()
        }}
      >
        <fieldset className="stack stack--ohne">
          <legend className="field__label">{t.scope}</legend>
          {(['all', 'readers'] as const).map((value) => (
            <label key={value} className="choice">
              <input
                type="radio"
                name="maintenance-scope"
                value={value}
                checked={scope === value}
                disabled={busy}
                onChange={() => setScope(value)}
                data-maintenance-scope={value}
              />
              <span>
                <span className="fett">{value === 'all' ? t.scopeAll : t.scopeReaders}</span>
                {value === 'readers' && (
                  <>
                    <br />
                    <span className="feld__hinweis klein">{t.scopeReadersHint}</span>
                  </>
                )}
              </span>
            </label>
          ))}
        </fieldset>

        <label className="field">
          <span className="field__label">{t.untilField}</span>
          <input
            className="field__input"
            type="datetime-local"
            value={until}
            disabled={busy}
            onChange={(event) => setUntil(event.target.value)}
            data-maintenance-until-field=""
          />
        </label>

        <label className="field">
          <span className="field__label">{t.sentence}</span>
          <textarea
            className="field__input"
            rows={3}
            value={text}
            disabled={busy}
            onChange={(event) => setText(event.target.value)}
            data-maintenance-sentence=""
          />
          <span className="feld__hinweis" data-maintenance-count={length}>
            {t.count(length, limits.maintenance_text)}
          </span>
        </label>

        {!online && (
          <p className="feld__hinweis" role="alert">
            {texte.allgemein.ohneNetz}
          </p>
        )}
        {error !== null && (
          <p className="notice" role="alert" data-maintenance-error="">
            {fehlertext(error)}
          </p>
        )}

        <button
          type="submit"
          className="button button--outline button--schmal"
          disabled={!online || busy || tooLong}
          data-maintenance-on=""
        >
          {active ? t.save : t.switchOn}
        </button>
        {active && (
          <button
            type="button"
            className="button button--outline button--schmal"
            disabled={!online || busy}
            data-maintenance-off=""
            onClick={async () => {
              if (await rueckfrage(t.askOff, t.askOffShort)) switchOff.mutate()
            }}
          >
            {t.switchOff}
          </button>
        )}
      </form>

      <p className="leise">{t.exit}</p>
    </div>
  )
}
